import {
  ComboboxData,
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  Stack,
} from "@mantine/core"

import { CountryLabel } from "../../CountryLabel/CountryLabel.tsx"
import { Select } from "../Select/Select.tsx"

import {
  CountryIso3166,
  CountryIso3166Enum,
} from "./CountriesSelector.types.ts"

export type CountryMapper = Partial<{
  [Country in CountryIso3166]: {
    countryName: string
    emoji: string
    code: Country
    priority: number
  }
}>

type Props = Omit<
  React.ComponentProps<typeof Select>,
  "data" | "value" | "onChange"
> & {
  selectCountries?: CountryIso3166[]
  countriesMapper: CountryMapper
  value: string | null
  onChange: (value: string | null) => void
}

function getSelectCountriesFromCountryMapper(
  selectCountries: CountryIso3166[],
  countriesMapper: CountryMapper
) {
  return selectCountries
    .map((country) => {
      const countryData = countriesMapper[country]
      if (countryData) {
        return {
          label: countryData.countryName,
          value: countryData.code,
        }
      }
      return null
    })
    .filter(Boolean)
}

function getSortedCountryMapperCountries(countriesMapper: CountryMapper) {
  return Object.values(countriesMapper)
    .sort((c1, c2) => c2.priority - c1.priority)
    .map((country) => ({
      label: `${country.countryName}`,
      value: country.code,
    }))
}

export const CountrySelector = ({
  selectCountries,
  value,
  onChange,
  countriesMapper,
  ...props
}: Props) => {
  let countries: ComboboxData = []
  if (selectCountries) {
    countries = getSelectCountriesFromCountryMapper(
      selectCountries,
      countriesMapper
    )
  } else {
    countries = getSortedCountryMapperCountries(countriesMapper)
  }
  return (
    <Select
      data={countries}
      value={value}
      onChange={onChange}
      customRender={({
        option,
        checked,
      }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
        if (!option) return null
        if (
          Object.values(CountryIso3166Enum).includes(
            option.value as CountryIso3166
          )
        ) {
          const countryLabel: CountryIso3166 = option.value as CountryIso3166
          return (
            <Stack
              px="var(--tfg-molecule-padding-x)"
              py="var(--tfg-molecule-padding-y)"
              data-checked={checked ? "true" : undefined}
            >
              <CountryLabel
                countryIso3166={countryLabel}
                countriesMapper={countriesMapper}
              />
            </Stack>
          )
        } else {
          return null
        }
      }}
      {...props}
    />
  )
}
